/* eslint-disable */
/**
 * @deprecated reportService.ts — PHASE 3 DISABLED
 *
 * MODULE_REPORTS=false in .env (backend + frontend).
 * Multipurpose reporting is soft-disabled. Only consultation-related
 * clinical stats remain in dashboard (getQuickStats, getConsultationStats).
 *
 * Rollback: set VITE_MODULE_REPORTS=true + MODULE_REPORTS=true + config:clear
 */
import apiClient from './apiClient';

// ==================== Types ====================

export interface ReportDashboard {
    period: { start: string; end: string };
    admissions: {
        total: number;
        active: number;
        by_day: Array<{ date: string; count: number }>;
        by_status: Array<{ name: string; count: number }>;
    };
    patients: {
        total: number;
        new: number;
    };
    consultations?: { total: number };
    appointments?: { total: number };
    laboratory: {
        total: number;
        completed: number;
    };
    imaging: {
        total: number;
    };
    financial: {
        revenue: number;
        discounts: number;
        invoice_revenue?: number;
        total_invoices?: number;
        paid_invoices?: number;
    };
}

export interface BillingReport {
    period: { start: string; end: string };
    summary: {
        total: number;
        paid: number;
        draft: number;
        cancelled: number;
        revenue: number;
        avg_invoice: number;
    };
    by_day: Array<{ date: string; count: number; revenue: number }>;
    by_payment_method: Array<{ payment_method: string; count: number; total: number }>;
    by_doctor: Array<{ first_name: string; last_name: string; total_invoices: number; revenue: number }>;
}

export interface AdmissionReport {
    data: any[];
    summary: {
        total: number;
        by_type: Array<{ name: string; count: number }>;
        by_priority: Array<{ priority: string; count: number }>;
    };
    pagination: any;
}

export interface FinancialReport {
    period: { start: string; end: string };
    summary: {
        total_closures: number;
        services: number;
        medications: number;
        supplies: number;
        honoraries: number;
        discounts: number;
        returns: number;
        grand_total: number;
    };
    by_day: Array<{ date: string; total: number; count: number }>;
}

export interface ReportParams {
    start_date?: string;
    end_date?: string;
    status_id?: number;
    doctor_id?: number;
}

// ==================== Report Service ====================

export const reportService = {
    async getDashboard(params: ReportParams = {}): Promise<ReportDashboard> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<ReportDashboard>>(`/reports/dashboard?${queryParams.toString()}`);
        return response.data.data;
    },

    async getAdmissions(params: ReportParams = {}): Promise<AdmissionReport> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.status_id) queryParams.append('status_id', params.status_id.toString());
        if (params.doctor_id) queryParams.append('doctor_id', params.doctor_id.toString());

        const response = await apiClient.get<ApiResponse<AdmissionReport>>(`/reports/admissions?${queryParams.toString()}`);
        return response.data.data;
    },

    async getPatients(params: ReportParams = {}): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<any>>(`/reports/patients?${queryParams.toString()}`);
        return response.data.data;
    },

    async getFinancial(params: ReportParams = {}): Promise<FinancialReport> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<FinancialReport>>(`/reports/financial?${queryParams.toString()}`);
        return response.data.data;
    },

    async getServices(params: ReportParams = {}): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<any>>(`/reports/services?${queryParams.toString()}`);
        return response.data.data;
    },

    async getLaboratory(params: ReportParams = {}): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<any>>(`/reports/laboratory?${queryParams.toString()}`);
        return response.data.data;
    },

    async getImaging(params: ReportParams = {}): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<any>>(`/reports/imaging?${queryParams.toString()}`);
        return response.data.data;
    },

    async getNursing(params: ReportParams = {}): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<any>>(`/reports/nursing?${queryParams.toString()}`);
        return response.data.data;
    },

    async getBilling(params: ReportParams = {}): Promise<BillingReport> {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);

        const response = await apiClient.get<ApiResponse<BillingReport>>(`/reports/billing?${queryParams.toString()}`);
        return response.data.data;
    },
};

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export default reportService;
